import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Star, History, Users } from "lucide-react";
import { useFollowedTeams } from "@/components/KickoffAlerts";

const REL = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d < 30 ? `${d}d ago` : new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [moves, setMoves] = useState([]);
  const { followed, toggle } = useFollowedTeams();

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data));
    api.get("/players").then((r) => setPlayers(r.data));
    api.get("/team-moves?limit=200").then((r) => setMoves(r.data || [])).catch(() => setMoves([]));
  }, []);

  // Group moves by team, newest first (endpoint already sorts).
  const movesByTeam = useMemo(() => {
    const g = {};
    for (const m of moves) {
      (g[m.team_id] ||= []).push(m);
    }
    return g;
  }, [moves]);

  return (
    <div className="space-y-8" data-testid="teams-root">
      <div>
        <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs mb-2">/ THE FRANCHISES</div>
        <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Teams</h1>
        <p className="text-gray-400 mt-2 text-sm">
          Tap the star to follow — get a browser ping 5 min before their next kickoff.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {teams.map((t) => {
          const roster = players.filter((p) => p.team_id === t.id);
          const avgRating = roster.length
            ? (roster.reduce((s, p) => s + (p.rating || 0), 0) / roster.length).toFixed(2)
            : "—";
          const isFollowing = followed.has(t.id);
          return (
            <div
              key={t.id}
              data-testid={`team-card-${t.tag}`}
              className="border border-[#232330] bg-[#101015] p-6 hover:-translate-y-0.5 transition-transform"
              style={{ borderLeft: `4px solid ${t.logo_color}` }}
            >
              <div className="flex items-start justify-between mb-4 gap-3">
                <div className="min-w-0">
                  <div className="font-heading text-2xl font-black uppercase tracking-tight truncate">{t.name}</div>
                  <div className="font-mono text-xs text-gray-500 tracking-widest">
                    {t.tag} · {t.game}
                    {t.region ? <span className="ml-2 px-1.5 py-0.5 border border-cyan-400/60 text-cyan-400" data-testid={`team-region-${t.tag}`}>{t.region.toUpperCase()}</span> : null}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <button
                    onClick={() => toggle(t.id)}
                    data-testid={`follow-team-${t.tag}`}
                    aria-label={isFollowing ? "Unfollow team" : "Follow team"}
                    className={`p-1.5 border transition-colors ${
                      isFollowing
                        ? "border-[#F4D03F] text-[#F4D03F] bg-[#F4D03F]/10"
                        : "border-[#232330] text-gray-500 hover:border-[#F4D03F] hover:text-[#F4D03F]"
                    }`}
                  >
                    <Star className={`w-4 h-4 ${isFollowing ? "fill-current" : ""}`} />
                  </button>
                  <div className="text-right">
                    <div className="text-[10px] text-gray-500 uppercase tracking-widest">Points</div>
                    <div className="font-heading font-black text-2xl text-cyan-400">{t.points}</div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-4">
                {[
                  { l: "W", v: t.wins || 0 }, { l: "D", v: t.draws || 0 }, { l: "L", v: t.losses || 0 },
                  { l: "RTG", v: avgRating },
                ].map((s) => (
                  <div key={s.l} className="border border-[#232330] p-2">
                    <div className="text-[9px] text-gray-500 font-mono tracking-widest">{s.l}</div>
                    <div className="font-mono font-bold text-sm text-white">{s.v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5">
                <Link
                  to={`/teams/${t.id}`}
                  data-testid={`team-roster-link-${t.tag}`}
                  className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-cyan-400 border border-[#232330] hover:border-cyan-400 px-3 py-1.5 transition-colors"
                >
                  <Users className="w-3 h-3" />
                  View full roster · {roster.length} player{roster.length === 1 ? "" : "s"}
                </Link>
              </div>
              {t.manager && (
                <div className="mt-4 text-xs text-gray-500 font-mono">Mgr: {t.manager}</div>
              )}
              {(movesByTeam[t.id] || []).length > 0 && (
                <div
                  data-testid={`team-moves-${t.tag}`}
                  className="mt-4 pt-4 border-t border-[#232330] space-y-1"
                >
                  <div className="text-[9px] font-mono uppercase tracking-widest text-gray-500 flex items-center gap-1">
                    <History className="w-3 h-3 text-cyan-400" />
                    Division history
                  </div>
                  {(movesByTeam[t.id] || []).slice(0, 3).map((m) => (
                    <div
                      key={m.id}
                      data-testid={`team-move-${t.tag}-${m.id}`}
                      className="text-[11px] font-mono text-gray-400 leading-snug"
                    >
                      <span className="text-gray-500">·</span>{" "}
                      Moved from <span className="text-gray-300">{m.from_division}</span>
                      {m.from_tier && m.to_tier && m.from_tier !== m.to_tier && (
                        <span className="text-gray-600"> ({m.from_tier})</span>
                      )}{" "}
                      → <span className="text-cyan-400">{m.to_division}</span>
                      {m.from_tier && m.to_tier && m.from_tier !== m.to_tier && (
                        <span className="text-[#F4D03F]"> ({m.to_tier})</span>
                      )}{" "}
                      <span className="text-gray-500">{REL(m.moved_at)}</span>
                      {m.moved_by && (
                        <span className="text-gray-500"> by <span className="text-gray-400">{m.moved_by}</span></span>
                      )}
                    </div>
                  ))}
                  {(movesByTeam[t.id] || []).length > 3 && (
                    <div className="text-[9px] font-mono uppercase tracking-widest text-gray-600">
                      +{(movesByTeam[t.id] || []).length - 3} earlier
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
